/* Detects list-extractor browser extensions (Listly and the like) and lets the
 * catalogue screens hide their rows while one is present.
 *
 * Worth being honest about what this is. The rate limiter in the API bounds how
 * fast an account can walk the catalogue over HTTP; it cannot see this case at
 * all, because an extension reads the DOM that was already rendered for a
 * signed-in customer - no extra request is made, so there is nothing for the
 * server or for Cloudflare to count or to block. This runs in the page instead,
 * and its whole claim is that it stops the casual user of an off-the-shelf
 * tool. Anyone willing to disable JavaScript, read the JSON the app already
 * fetched, or rename the extension's own elements walks straight past it. It is
 * a speed bump, deliberately, and it is the only kind of bump available on this
 * side of the wire.
 *
 * Detection is by name, not by behaviour. A DOM read cannot be observed, and
 * the generic tell - "some element appeared under <body> that React did not
 * render" - is true of password managers, translators and ad blockers too, so
 * using it would blank the catalogue for paying customers running perfectly
 * ordinary extensions. A false positive here costs a sale; a false negative
 * costs nothing that was not already lost. So the list below is narrow on
 * purpose and the guard stays quiet unless it matches.
 */

/* Substrings matched case-insensitively against an element's tag name, id,
 * class list and a couple of attributes.
 *
 * IMPORTANT: `listly` is a best-effort guess. Listly's injected markup is not
 * documented publicly and could not be confirmed from outside the extension, so
 * this entry is unverified. Confirm it before trusting the guard: install
 * Listly, open the catalogue, start an extraction, then in DevTools run
 *
 *   $$('body > *').map((n) => n.tagName + '#' + n.id + '.' + n.className)
 *
 * and add whatever the extension actually injects. Everything else here is a
 * common scraper extension named the same way. Keep the entries specific -
 * a short or generic substring ('app', 'panel', 'data') will match a customer's
 * unrelated extension and hide the catalogue from them.
 */
export const SCRAPER_SIGNATURES = [
  'listly',
  'web-scraper',
  'webscraper',
  'instant-data-scraper',
  'instantdatascraper',
  'data-miner',
  'dataminer',
  'simplescraper',
  'scraper-extension',
];

/* Same shape as RATE_LIMIT_ENABLED in the API: every spelling of "off" works,
 * and anything else - including an empty value - leaves the guard on. If this
 * ever hides the catalogue from a real customer, this is the switch, and it
 * costs a rebuild rather than a code change.
 */
export const isScrapeGuardEnabled = () =>
  !['false', '0', 'off', 'no'].includes(String(process.env.REACT_APP_SCRAPE_GUARD ?? '').trim().toLowerCase());

/* Attributes checked besides tag/id/class. Extensions that render into a shadow
 * root leave nothing readable inside it, but the host element they attach it to
 * is in our document and is usually named after the tool.
 */
const CHECKED_ATTRIBUTES = ['data-extension', 'data-app', 'name'];

const describe = (element) => {
  const parts = [element.tagName, element.id];
  /* className is an SVGAnimatedString on SVG elements, where `.toLowerCase` does
     not exist - `classList` is a DOMTokenList on both. */
  parts.push(...Array.from(element.classList || []));
  CHECKED_ATTRIBUTES.forEach((attribute) => {
    const value = element.getAttribute?.(attribute);
    if (value) parts.push(value);
  });
  return parts.filter(Boolean).join(' ').toLowerCase();
};

export const isScraperElement = (element) => {
  if (!element || element.nodeType !== Node.ELEMENT_NODE) return false;
  const haystack = describe(element);
  return SCRAPER_SIGNATURES.some((signature) => haystack.includes(signature));
};

/* Only the top of the tree is searched, not every node on the page. An
 * extension panel is mounted at or near <body>, so there is no reason to sweep
 * the document; but the whole of an inserted subtree has to be searched, not
 * just its top.
 *
 * `subtree: true` does not help here. A mutation record lists a node as added
 * only at the moment it, or an ancestor already in the tree, receives it - so an
 * extension that builds its panel off-DOM and inserts the finished thing in one
 * appendChild produces exactly one record, naming the outer wrapper. Checking
 * that wrapper and one level of children then misses
 * `<div class="ext-root"><div class="toolbar"><button id="web-scraper-start">`
 * entirely, and building complete UI before a single insert is the common
 * extension pattern rather than an edge case. One level deep was not a cheaper
 * search, it was a search that would mostly not find anything.
 *
 * The cap belongs to the mutation path and only to it. A freshly inserted
 * subtree is scanned in one pass and an extension's own markup is near the top
 * of its own panel, so stopping after MAX_SCANNED_NODES cannot lose it; what the
 * cap buys is that one enormous insert cannot make the page stutter.
 *
 * The startup sweep is the opposite case and must not be capped. It walks
 * `document.body` in document order, and an extension appends its panel as the
 * last child of <body> - after the app's own root. A catalogue page renders
 * DEFAULT_PAGE_SIZE (100) rows of nine columns, each with nested spans and
 * images, which clears 2000 nodes on its own; the sweep would then stop among
 * the app's own table cells and never reach the panel sitting right there at the
 * end. Nothing would rescue it either, because markup that was already present
 * at mount produces no mutation to observe. On exactly the fully-populated
 * catalogue this guard exists to protect, a capped sweep reports "not detected"
 * with the extension in plain sight.
 *
 * Uncapped is affordable precisely because it happens once per mount rather than
 * per mutation.
 */
const MAX_SCANNED_NODES = 2000;

const findScraper = (root, { limit = MAX_SCANNED_NODES } = {}) => {
  if (isScraperElement(root)) return root;
  if (!root?.querySelectorAll) return null;

  const descendants = root.querySelectorAll('*');
  const scanned = Math.min(descendants.length, limit);
  for (let index = 0; index < scanned; index += 1) {
    if (isScraperElement(descendants[index])) return descendants[index];
  }
  return null;
};

/**
 * Calls `onDetected` once, when an extractor extension is seen - either already
 * present at startup or injected later. Returns a function that stops watching.
 *
 * Never fires again after the first detection: the callback hides the rows, and
 * re-running it on every subsequent mutation would be work with no effect.
 */
export const startScrapeGuard = (onDetected) => {
  if (!isScrapeGuardEnabled() || typeof MutationObserver === 'undefined') return () => {};

  let fired = false;
  const fire = () => {
    if (fired) return;
    fired = true;
    observer.disconnect();
    onDetected();
  };

  const observer = new MutationObserver((mutations) => {
    const found = mutations.some((mutation) =>
      Array.from(mutation.addedNodes).some((node) => findScraper(node))
    );
    if (found) fire();
  });

  /* The extension may have injected before React mounted, in which case no
     mutation is ever observed and only this initial sweep sees it - so it runs
     uncapped, for the reasons above. */
  if (findScraper(document.body, { limit: Infinity })) {
    fire();
    return () => {};
  }

  observer.observe(document.documentElement, { childList: true, subtree: true });
  return () => observer.disconnect();
};
